import { Button, Modal } from '../ui'
import { Icon } from '../ui/icons'
import { Logo } from '../brand/Logo'
import { CLINICAL_ORDER_TYPE_LABELS, formatDateTime, formatRut, HEALTH_ROLE_LABELS } from '../../utils/format'
import { DocumentIdentityFooter } from './DocumentIdentityFooter'
import type { ClinicalOrder } from '../../types/api'

/**
 * Impreso de un documento clínico: receta, solicitud de exámenes u orden de
 * kinesiología. Se imprime desde la misma pantalla — la regla `.print-document`
 * de `styles/index.css` oculta todo lo demás — para que emitir sea un clic.
 *
 * Lo usan el médico al emitirlo en la ficha y el paciente al abrirlo en su
 * portal: es el mismo papel, con el mismo código de verificación, y por eso
 * vive en `components/print/` y no dentro de una de las dos pantallas.
 */

const TITLES = CLINICAL_ORDER_TYPE_LABELS

/** Posología en una línea, saltando lo que el profesional no completó. */
function posology(item: ClinicalOrder['items'][number]): string {
  return [item.dose, item.route, item.frequency, item.duration].filter(Boolean).join(' · ')
}

export function PrintableOrder({ order, onClose }: { order: ClinicalOrder; onClose: () => void }) {
  const location = order.encounter?.appointment?.location

  return (
    <Modal title={TITLES[order.type]} onClose={onClose} wide>
      <div className="print-document space-y-5 bg-white text-ink">
        {/* Encabezado */}
        <header className="flex items-start justify-between gap-4 border-b border-steel-200 pb-4">
          <div>
            <Logo className="h-11" />
            <p className="mt-1 text-xs text-steel-600">
              {location?.name ?? 'Cima Sport'}
              {location?.address ? ` · ${location.address}` : ''}
            </p>
            {location?.phone && <p className="text-xs text-steel-600">{location.phone}</p>}
          </div>
          <div className="text-right">
            <p className="font-display text-lg font-bold text-ink">{TITLES[order.type]}</p>
            <p className="font-mono text-xs text-steel-600">{order.verificationCode}</p>
            <p className="text-xs text-steel-600">{formatDateTime(order.issuedAt)}</p>
          </div>
        </header>

        {/* Paciente */}
        <section className="grid gap-1 text-sm sm:grid-cols-2">
          <p>
            <span className="font-semibold">Paciente:</span> {order.patient.firstName} {order.patient.lastName}
          </p>
          <p>
            <span className="font-semibold">RUT:</span> {formatRut(order.patient.rut)}
          </p>
        </section>

        {/* Contenido según el tipo */}
        {order.type === 'KINESIOLOGY' ? (
          <section className="space-y-2 text-sm">
            <p>
              <span className="font-semibold">Diagnóstico kinésico:</span> {order.kinesicDiagnosis}
            </p>
            <p>
              <span className="font-semibold">Sesiones indicadas:</span> {order.sessions}
            </p>
          </section>
        ) : (
          <section>
            <ol className="space-y-2 text-sm">
              {order.items.map((item, i) => (
                <li key={item.id} className="border-b border-dashed border-steel-100 pb-2 last:border-0">
                  <p className="font-semibold text-ink">
                    {i + 1}. {item.name}
                    {item.quantity ? <span className="font-normal text-steel-600"> — {item.quantity}</span> : null}
                  </p>
                  {posology(item) && <p className="text-steel-700">{posology(item)}</p>}
                  {item.notes && <p className="text-steel-600">{item.notes}</p>}
                </li>
              ))}
            </ol>
          </section>
        )}

        {order.notes && (
          <section className="text-sm">
            <p className="font-semibold">Indicaciones generales</p>
            <p className="whitespace-pre-line text-steel-700">{order.notes}</p>
          </section>
        )}

        {/* Firma del profesional */}
        <section className="pt-8 text-sm">
          <div className="ml-auto w-64 border-t border-steel-400 pt-1 text-center">
            <p className="font-semibold text-ink">
              {order.professional.firstName} {order.professional.lastName}
            </p>
            <p className="text-xs text-steel-600">
              {order.professional.specialty ?? HEALTH_ROLE_LABELS[order.professional.healthRole]}
            </p>
            {order.professional.rut && <p className="text-xs text-steel-600">RUT {formatRut(order.professional.rut)}</p>}
            {order.professional.registryNumber && (
              <p className="text-xs text-steel-600">Registro {order.professional.registryNumber}</p>
            )}
          </div>
        </section>

        <DocumentIdentityFooter verificationCode={order.verificationCode} kind="clinical-order" />
      </div>

      <div className="mt-4 flex gap-2 print:hidden">
        <Button variant="cta" onClick={() => window.print()}>
          <Icon name="print" className="mr-1.5 inline h-4 w-4" />
          Imprimir
        </Button>
        <Button variant="ghost" onClick={onClose}>Cerrar</Button>
      </div>
    </Modal>
  )
}
